// Trabajo / presupuesto de VARIOS equipos, diseño limpio (18-oct-2026). Pensado para 1 a 100+ equipos
// (Sanatorio Güemes): antes era una hoja por equipo, ahora
//   hoja 1: cliente | alcance → resumen por concepto → total → detalle por equipo (un renglón c/u)
//   anexo:  solo los equipos con fotos, uno por fila con sus fotos chicas y su rótulo.
import { cargarFoto, fitEnCaja } from '../helpers.js';
import { montosDescuento, construirFilasItem } from '../pdfShared.js';
import { ubicacionLegible } from '../porEquipo.js';
import { M, CONTENT_W } from '../theme.js';
import { F, K, texto, rotulo, plata, linea, datos, parrafo, tabla, totales, firmas, colCliente, espacio } from './base.js';
import { GARANTIA, condicionesPresupuesto } from './unEquipo.js';

const num = (s) => Number(String(s).replace(/\./g, '').replace(',', '.')) || 0;
const trabajoDe = (it) => String(it.trabajo || it.trabajoRealizado || it.resumenTexto || '').replace(/\s+/g, ' ').trim();
const totalDe = (it) => parseFloat(it.totalCalculado || it.costo || 0) || 0;
const tieneFotos = (it) => !!(it.fotoAntes || it.fotoDespues || (it.fotosExtra || []).length);

// Resumen: los mismos conceptos de todos los equipos sumados ("Mano de obra × 100", "Filtro × 80").
// 18-oct-2026: no se agrupa por el texto que escribió el técnico ("Limpieza y cambio de Filtro",
// "...de filtro.", "...TIENE DOS O TRES CUCAS!" salían en renglones distintos): la mano de obra va
// como "Mano de obra" por precio, y los repuestos se juntan sin importar mayúsculas, acentos ni
// espacios. El texto de cada equipo queda en el detalle por equipo.
const clave = (t) => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ').trim();
function resumenPorConcepto(items) {
    const grupos = new Map();
    items.forEach(it => {
        construirFilasItem(it).forEach(f => {
            const concepto = f.esServicio ? (it.esVisita ? 'Visita y diagnóstico' : 'Mano de obra') : f.concepto;
            const unit = num(f.unitario);
            const k = `${f.esServicio ? (it.esVisita ? '#visita' : '#mo') : clave(concepto)}|${unit}`;
            const g = grupos.get(k) || { concepto, unit, cant: 0, importe: 0, nombres: new Map(), mo: f.esServicio };
            g.cant += Number(f.cant) || 0;
            g.importe += num(f.importe);
            g.nombres.set(concepto, (g.nombres.get(concepto) || 0) + 1);
            grupos.set(k, g);
        });
    });
    // Nombre más usado de cada grupo; la mano de obra primero
    return [...grupos.values()]
        .map(g => {
            const n = [...g.nombres.entries()].sort((x, y) => y[1] - x[1])[0][0].replace(/\s+/g, ' ').trim();
            return { ...g, concepto: n.charAt(0).toUpperCase() + n.slice(1) };
        })
        .sort((x, y) => (y.mo - x.mo) || (y.importe - x.importe));
}

export async function generarVariosV2(doc, {
    ctx, ticketItems: items, cliente, sede, y, esPresupuesto = false, soloResumen = false,
    firmaCliente, firmaTecnico, aclaracionCliente = '', aclaracionTecnico = '',
    descuentoPorcentaje = 0, descuentoAlcance = 'TOTAL', totales: tot = null, garantiaTexto = null,
    proximoMantenimiento = null, leyenda = '', sinPrecios = false,
    condicionIva = null, pctEfectivo = 0, cobradoEfectivo = false, montoCobrado = null,
}) {
    const conFotos = items.filter(tieneFotos).length;
    y = datos(doc, y, colCliente(cliente, sede), {
        rotulo: 'Alcance', nombre: `${items.length} equipos`,
        lineas: [conFotos ? `${conFotos} con fotos (al final)` : null],
    });

    // Resumen por concepto + total
    const resumen = resumenPorConcepto(items);
    if (resumen.length) {
        rotulo(doc, 'Resumen', M, y);
        y = tabla(doc, ctx, y + 2, sinPrecios
            ? [{ titulo: 'Concepto' }, { titulo: 'Cant.', ancho: 16, derecha: true }]
            : [{ titulo: 'Concepto' }, { titulo: 'Cant.', ancho: 14, derecha: true }, { titulo: 'Unitario', ancho: 30, derecha: true }, { titulo: 'Importe', ancho: 32, derecha: true }],
            resumen.map(g => (sinPrecios ? [g.concepto, String(g.cant)] : [g.concepto, String(g.cant), plata(g.unit), plata(g.importe)])));
    }
    if (!sinPrecios) {
        const totalBruto = items.reduce((s, it) => s + totalDe(it), 0);
        const md = montosDescuento({ totalBruto, descuentoPorcentaje, descuentoAlcance, totales: tot });
        y = totales(doc, ctx, y, {
            subtotal: md.subtotal, descuentos: md.descuento > 0 ? [{ label: md.label, monto: md.descuento }] : [], total: md.total,
            condicionIva, esPresupuesto, pctEfectivo, cobradoEfectivo, montoCobrado,
            notas: [garantiaTexto || GARANTIA, proximoMantenimiento ? `Próximo mantenimiento sugerido: ${proximoMantenimiento}` : null],
        });
    }
    if (esPresupuesto && !sinPrecios) y = parrafo(doc, ctx, y, 'Condiciones', condicionesPresupuesto(false), { maxRenglones: 3 });
    if (String(leyenda || '').trim() && !/90 d.*mano de obra/i.test(leyenda)) y = parrafo(doc, ctx, y, 'Observaciones', leyenda, { maxRenglones: 4 });

    // Detalle por equipo: un renglón cada uno
    y = espacio(doc, ctx, y + 2, 20);
    rotulo(doc, 'Detalle por equipo', M, y);
    const filas = items.map((it, i) => {
        const reps = (it.repuestosUsados || []).map(r => `${Number(r.cantidad) > 1 ? `${r.cantidad}× ` : ''}${r.nombre}`).join(', ');
        const hecho = [trabajoDe(it), reps].filter(Boolean).join(' · ') || '—';
        const serie = it.equipoSerial && it.equipoSerial !== 'MOSTRADOR' ? it.equipoSerial : '—';
        const base = [String(i + 1), serie, ubicacionLegible(it) || '—', hecho];
        return sinPrecios ? base : [...base, plata(totalDe(it))];
    });
    y = tabla(doc, ctx, y + 2, [
        { titulo: '#', ancho: 8 }, { titulo: 'N/S', ancho: 26 }, { titulo: 'Ubicación', ancho: 40 }, { titulo: esPresupuesto ? 'A realizar' : 'Realizado' },
        ...(sinPrecios ? [] : [{ titulo: 'Importe', ancho: 24, derecha: true }]),
    ], filas, { negritaCol: 1, tamCuerpo: 7.5 });

    // Firmas
    y = firmas(doc, ctx, y + 6, { firmaCliente, firmaTecnico, aclaracionCliente, aclaracionTecnico });

    // Anexo: fotos por equipo (solo los que tienen)
    if (!soloResumen && conFotos) await anexoFotos(doc, ctx, y + 4, items, esPresupuesto);
    return y;
}

const ANCHO_DATOS = 40;
const GAP = 3;
const ALTO_FOTO = 30;
async function anexoFotos(doc, ctx, y, items, esPresupuesto) {
    y = espacio(doc, ctx, y, 12 + ALTO_FOTO + 8);
    rotulo(doc, esPresupuesto ? 'Fotos de los equipos' : 'Fotos por equipo', M, y);
    y += 4;
    const anchoFoto = (CONTENT_W - ANCHO_DATOS - GAP * 4) / 4;
    for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (!tieneFotos(it)) continue;
        const rot = it.fotosRotulos;
        const fuentes = [
            [it.fotoAntes, rot ? rot.antes : (esPresupuesto ? null : 'Estado inicial')],
            [it.fotoDespues, rot ? rot.despues : (esPresupuesto ? null : 'Estado final')],
            ...(it.fotosExtra || []).slice(0, 2).map((f, k) => [f, rot ? (rot.extra || [])[k] : null]),
        ].filter(([f]) => f);
        // De a un equipo, en baja resolución: con 100 equipos no se llena la memoria del celu
        const fotos = await Promise.all(fuentes.map(([f]) => cargarFoto(f, 500, 0.7).catch(() => null))); // eslint-disable-line no-await-in-loop
        const alto = ALTO_FOTO + 9;
        y = espacio(doc, ctx, y, alto);
        texto(doc, it.equipoSerial || '—', M, y + 4, { tam: F.texto, negrita: true });
        texto(doc, ubicacionLegible(it) || '', M, y + 8, { tam: F.chico, color: K.gris, ancho: ANCHO_DATOS - 2 });
        const yFila = y;
        fuentes.forEach(([, label], k) => {
            const x = M + ANCHO_DATOS + k * (anchoFoto + GAP);
            const foto = fotos[k];
            doc.setFillColor(238, 238, 241);
            doc.roundedRect(x, yFila + 1, anchoFoto, ALTO_FOTO, 1.5, 1.5, 'F');
            if (foto) {
                const { w, h } = fitEnCaja(foto.w, foto.h, anchoFoto, ALTO_FOTO);
                try { doc.addImage(foto.data, foto.format, x + (anchoFoto - w) / 2, yFila + 1 + (ALTO_FOTO - h) / 2, w, h); } catch { /* foto rota */ }
            }
            rotulo(doc, String(label || `Foto ${k + 1}`), x + anchoFoto / 2, yFila + ALTO_FOTO + 5, { alinear: 'center' });
        });
        y += alto;
        linea(doc, y - 1);
        y += 2;
    }
    return y;
}

